import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { deviceService } from '@/services/api/services/deviceService';
import { Device } from '@/types/device.types';
import { DeviceTable } from '@/components/features/devices/DeviceTable';
import { Plus, Smartphone, RefreshCw } from 'lucide-react';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay';
import { usePermissions } from '@/contexts/PermissionsContext';
import { PERMISSIONS } from '@/contexts/PermissionsContext';
import { useNavigationHelpers } from '@/utils/navigationHelpers';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';

export const DeviceManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { navigateWithTokenCheck } = useNavigationHelpers();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [devices, setDevices] = useState<Device[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCheckingSecurity, setIsCheckingSecurity] = useState(true);

  // Security validation function
  const validateSecurityAccess = () => {
    if (!user?.id) {
      showToast(t('errors.userNotAuthenticated'), 'error');
      navigate('/');
      return;
    }

    // Only Administrators and Supervisors can access device management
    if (user.role !== 'Administrador' && user.role !== 'Supervisor') {
      showToast(t('errors.noAccessToDeviceManagement'), 'error');
      navigate('/');
      return;
    }

    setIsCheckingSecurity(false);
  };

  useEffect(() => {
    if (user?.id) {
      validateSecurityAccess();
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Only fetch devices if security check passed
    if (!isCheckingSecurity && (user?.role === 'Administrador' || user?.role === 'Supervisor')) {
      fetchDevices();
    }
  }, [isCheckingSecurity, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDevices = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      let response;
      
      // Si el usuario es Supervisor, solo obtener sus dispositivos asociados
      if (user?.role === 'Supervisor' && user?.id) {
        response = await deviceService.getByUserId(user.id);
      } else {
        // Para Administrador y otros roles, obtener todos los dispositivos
        response = await deviceService.getAll();
      }
      
      if (response.success) {
        setDevices(response.data);
      } else {
        setError(response.error.message || 'Failed to fetch devices');
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      setError('Failed to fetch devices. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (device: Device) => {
    // Edición de dispositivo - sin token check (ruta no importante)
    navigate(`/devices/edit/${device.id}`);
  };

  const handleDeactivate = async (id: string) => {
    try {
      const response = await deviceService.deactivate(id);
      if (response.success) {
        // Refresh the entire table instead of filtering
        await fetchDevices();
      } else {
        setError(response.error.message || 'Failed to deactivate device');
      }
    } catch (error) {
      console.error('Error deactivating device:', error);
      setError('Failed to deactivate device. Please try again later.');
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      const response = await deviceService.reactivate(id);
      if (response.success) {
        // Refresh the entire table
        await fetchDevices();
      } else {
        setError(response.error.message || 'Failed to reactivate device');
      }
    } catch (error) {
      console.error('Error reactivating device:', error);
      setError('Failed to reactivate device. Please try again later.');
    }
  };

  const handleManageUsers = async (device: Device) => {
    // Gestión de usuarios de dispositivo - con token check (ruta importante)
    await navigateWithTokenCheck(navigate, `/devices/${device.id}/users`);
  };

  // Show loading while checking security
  if (isCheckingSecurity) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-brand-white py-4">
      <LoadingOverlay isVisible={isLoading} message="Cargando dispositivos..." />

      <div
        className={`max-w-[100%] mx-auto space-y-6 ${isLoading ? 'blur-sm pointer-events-none' : ''}`}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Smartphone className="h-7 w-7 text-brand-orange" strokeWidth={1.5} />
            <div>
              <h1 className="text-3xl font-bold text-brand-orange">{t('pages.devices.title')}</h1>
              {user?.role === 'Supervisor' && (
                <p className="text-sm text-gray-600 mt-1">
                  {t('pages.devices.supervisorView')} - {devices.length} {t('pages.devices.associatedDevices')}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchDevices}
              disabled={isLoading}
              className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw
                className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`}
                strokeWidth={1.5}
              />
              {t('common.refresh')}
            </button>
            {hasPermission(PERMISSIONS.DEVICE_CREATE) && (
              <button
                onClick={() => navigate('/devices/new')}
                className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2"
              >
                <Plus className="h-5 w-5" strokeWidth={1.5} />
                {t('pages.devices.createNew')}
              </button>
            )}
          </div>
        </div>

        {error && (
          <div
            className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative"
            role="alert"
          >
            <strong className="font-bold">{t('common.error')}!</strong>
            <span className="block sm:inline"> {error}</span>
            <button
              className="mt-2 bg-red-100 hover:bg-red-200 text-red-800 font-semibold py-2 px-4 rounded"
              onClick={fetchDevices}
            >
              Retry
            </button>
          </div>
        )}

        <DeviceTable
          devices={devices}
          onEdit={handleEdit}
          onDeactivate={handleDeactivate}
          onReactivate={handleReactivate}
          onManageUsers={handleManageUsers}
        />
      </div>
    </div>
  );
};
